import { Alert, AlertDescription, AlertTitle } from "#components/ui/alert"
import { Button } from "#components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { Input } from "#components/ui/input"
import { Label } from "#components/ui/label"
import { useI18n } from "#hooks/use-i18n"
import { homeFinancialSDK } from "#sdk/sdk"
import { useVerifyEmail } from "@/hooks/use-auth"
import { CheckCircle2, Loader2, XCircle } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

export default function VerifyEmail() {
  const { t } = useI18n()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { mutateAsync: verifyEmailMutation } = useVerifyEmail()
  const token = searchParams.get("token")
  const emailParam = searchParams.get("email")

  const [status, setStatus] = useState<"loading" | "success" | "error" | "resend">(
    token ? "loading" : "resend"
  )
  const [resendStatus, setResendStatus] = useState<"success" | "error" | null>(null)
  const [message, setMessage] = useState<string>("")
  const [email, setEmail] = useState<string>(emailParam || "")
  const [isResending, setIsResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string>("")
  const hasVerifiedRef = useRef(false)

  useEffect(() => {
    if (!token) {
      setStatus("resend")
      return
    }

    if (hasVerifiedRef.current || status !== "loading") {
      return
    }

    hasVerifiedRef.current = true

    const verifyEmail = async () => {
      try {
        const message = await verifyEmailMutation(token)
        setStatus("success")
        setMessage(message || t("components.verifyEmail.success"))
      } catch (error) {
        setStatus("error")
        if (error instanceof Error) {
          setMessage(error.message)
        } else {
          setMessage(t("components.verifyEmail.error"))
        }
      }
    }

    verifyEmail()
  }, [token])

  const handleGoToLogin = () => {
    navigate("/login", { replace: true })
  }

  const handleGoToDashboard = () => {
    navigate("/dashboard", { replace: true })
  }

  const handleResendVerification = async () => {
    if (!email) {
      setResendMessage(t("components.verifyEmail.emailRequired"))
      return
    }

    setIsResending(true)
    setResendMessage("")

    try {
      await homeFinancialSDK.users.resendVerificationEmailByEmail(email)
      setResendMessage(t("components.verifyEmail.resendSuccess"))
      setResendStatus("success")
    } catch (error) {
      setResendMessage(
        error instanceof Error ? error.message : t("components.verifyEmail.resendError")
      )
      setResendStatus("error")
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          {status === "success" && (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-xl">{t("components.verifyEmail.successTitle")}</CardTitle>
              <CardDescription>{t("components.verifyEmail.successDescription")}</CardDescription>
            </>
          )}

          {status === "error" && (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-xl">{t("components.verifyEmail.errorTitle")}</CardTitle>
              <CardDescription>{t("components.verifyEmail.errorDescription")}</CardDescription>
            </>
          )}

          {status === "resend" && (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <CardTitle className="text-xl">{t("components.verifyEmail.verifying")}</CardTitle>
              <CardDescription>{t("components.verifyEmail.resendDescription")}</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {message && (
            <Alert className={status === "error" ? "border-red-500" : ""}>
              {status === "success" && <CheckCircle2 className="h-4 w-4" />}
              {status === "error" && <XCircle className="h-4 w-4" />}
              <AlertTitle>
                {status === "success"
                  ? t("components.verifyEmail.emailVerified")
                  : t("components.verifyEmail.verificationFailed")}
              </AlertTitle>
              <AlertDescription className="mt-2">{message}</AlertDescription>
            </Alert>
          )}

          <div className="mt-6 flex flex-col gap-2">
            {status === "success" && (
              <>
                <Button onClick={handleGoToDashboard} className="w-full">
                  {t("components.verifyEmail.goToDashboard")}
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <Button onClick={handleGoToLogin} className="w-full">
                  {t("components.verifyEmail.goToLogin")}
                </Button>
                <Button
                  onClick={() => setStatus("resend")}
                  variant="outline"
                  className="w-full mt-2"
                >
                  {t("components.verifyEmail.resendEmail")}
                </Button>
              </>
            )}

            {status === "resend" && (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("components.verifyEmail.emailLabel")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t("components.verifyEmail.emailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isResending}
                    />
                  </div>
                  {resendMessage && (
                    <Alert
                      className={resendStatus === "error" ? "border-red-500" : "border-primary"}
                    >
                      {resendStatus === "success" ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                      <AlertDescription>{resendMessage}</AlertDescription>
                    </Alert>
                  )}
                  <Button
                    onClick={handleResendVerification}
                    disabled={isResending || !email}
                    className="w-full"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("components.verifyEmail.sending")}
                      </>
                    ) : (
                      t("components.verifyEmail.resendButton")
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
